import { App, Col, Form, Row, Select } from "antd"
import { motion } from "motion/react"
import { useEffect, useMemo, useState } from "react"

import { classSessionAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import Modal from "#blocks/Modal"
import { ANALYTICS_EVENTS } from "#classes/FirebaseAnalytics"
import ClickableStudentInfoRow from "#components/ClickableStudentInfoRow"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { invariant } from "#customTypes/types"
import { useIsFulfilled } from "#hooks/jotai/useCascadingData"
import {
  useGetAllStudents,
  useGetOneStudent,
} from "#hooks/jotai/useGetStudents"
import { useSomeDocs, useStableIds } from "#hooks/jotai/useSubscription"
import useOptimizedFilter from "#hooks/newHooks/useOptimizedFilter"
import { useInvalidateCheckUsage } from "#hooks/reactQuery/useCheckUsage"
import { useFirebaseBatchUpdate } from "#hooks/reactQuery/useFirebaseMutation"
import useAnalytics from "#hooks/useAnalytics"
import { useErrorModal } from "#hooks/useErrorModal"
import { buildAttendanceUpdate } from "#utils/classHelpers"
import {
  getBlankAttendance,
  getNewAttendanceForClass,
  getStudentOptions,
} from "#utils/studentHelpers"
import { ErrorType } from "#utils/textFormatters"

import ClassSessionSelectionGrid from "../ClassSessionSelectionGrid"
import { useSessionSelectionState } from "../useSessionSelectionState"

const AddAssistantToClassForm = ({
  sessionIds,
  onClose,
  open,
  assistantId = null,
}: {
  sessionIds: string[]
  onClose: () => void
  open: boolean
  assistantId?: string | null
}) => {
  const isEdit = !!assistantId
  const { trackAssistantEnrollmentAction } = useAnalytics()

  const form = Form.useFormInstance()
  const className = Form.useWatch<string>("name", form)
  const classId = Form.useWatch<string>("id", form)

  const assistants = useGetAllStudents()

  const assistantOptions = useMemo(() => {
    return getStudentOptions(assistants)
  }, [assistants])

  const { filteredItems: filteredAssistantOptions, onSearch } =
    useOptimizedFilter(assistantOptions)

  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(
    assistantId
  )

  const selectedAssistant = useGetOneStudent(selectedAssistantId)

  const stableSessionIds = useStableIds(sessionIds)
  const classSessions = useSomeDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    stableSessionIds
  )
  const classSessionsFulfilled = useIsFulfilled(stableSessionIds, classSessions)

  const attendanceForClass = useMemo(() => {
    if (!stableSessionIds.length) return []

    const blankAttendance = getBlankAttendance(stableSessionIds)
    const attendance = selectedAssistant?.assistantAttendance?.[classId]

    return blankAttendance.map((session, i) => ({
      ...session,
      status:
        attendance?.[i]?.status &&
        attendance[i].status !== ATTENDANCE_STATUSES.INVALID
          ? attendance?.[i]?.status
          : session.status,
    }))
  }, [selectedAssistant, classId, stableSessionIds])

  const [selectedSessions, setSelectedSessions] = useState<boolean[]>([])

  useEffect(() => {
    setSelectedSessions(
      attendanceForClass?.map(
        ({ status }) =>
          status &&
          status !== ATTENDANCE_STATUSES.NOT_ENROLLED &&
          status !== ATTENDANCE_STATUSES.INVALID
      )
    )
  }, [attendanceForClass])

  const { newlySelectedSessions, droppedSessions } = useSessionSelectionState(
    selectedSessions,
    attendanceForClass
  )

  const sortedClassSessions = useMemo(() => {
    return classSessions?.slice().sort((a, b) => {
      return a.eventStart.localeCompare(b.eventStart)
    })
  }, [classSessions])

  // Build session metadata with disabled reasons
  const sessionMetadata = useMemo(() => {
    return attendanceForClass.map((attendance) => {
      const isDisabled =
        attendance.status !== ATTENDANCE_STATUSES.NOT_ENROLLED &&
        attendance.status !== ATTENDANCE_STATUSES.ENROLLED

      return {
        disabledReason: isDisabled
          ? "Session has already been attended"
          : undefined,
      }
    })
  }, [attendanceForClass])

  const { message } = App.useApp()

  const { mutate: updateClassSessionAttendance } = useFirebaseBatchUpdate([
    DOC_TYPES.CLASS_SESSIONS,
    DOC_TYPES.STUDENTS,
    DOC_TYPES.CLASSES,
  ])

  const [isPending, setIsPending] = useState(false)

  const recalculateClassUsage = useInvalidateCheckUsage(
    DOC_TYPES.CLASSES,
    classId
  )
  const openErrorModal = useErrorModal("adding assistant to class")

  const handleSubmit = async () => {
    setIsPending(true)
    try {
      invariant(selectedAssistantId != null, "Assistant not found")
      invariant(selectedAssistant != null, "Assistant not found")
      invariant(classSessionsFulfilled, "Sessions not found")

      const selectedSessionsWithIds = selectedSessions.map((isSelected, i) => {
        return {
          isSelected,
          sessionId: sortedClassSessions[i].id,
        }
      })
      const newAttendanceForClass = getNewAttendanceForClass(
        attendanceForClass,
        selectedSessionsWithIds
      )

      const {
        person,
        class: classData,
        classSessionsById,
      } = buildAttendanceUpdate(
        newAttendanceForClass,
        selectedSessionsWithIds,
        classId,
        selectedAssistantId,
        "assistant",
        selectedAssistant?.assistantAttendance
      )

      // Perform the update - Jotai subscriptions will automatically update atoms
      await updateClassSessionAttendance({
        [DOC_TYPES.STUDENTS]: {
          [selectedAssistantId]: person,
        },
        [DOC_TYPES.CLASSES]: {
          [classId]: classData,
        },
        [DOC_TYPES.CLASS_SESSIONS]: classSessionsById,
      })

      trackAssistantEnrollmentAction(
        ANALYTICS_EVENTS.ASSISTANT_ADDED_TO_CLASS,
        {
          studentId: selectedAssistantId,
          classId: classId,
          sessionCount: selectedSessions.filter(Boolean).length,
        }
      )

      // Invalidate usage query (now uses Jotai atoms)
      recalculateClassUsage()

      void message.success(`${selectedAssistant.name}'s attendance updated.`)

      onClose()
    } catch (error) {
      openErrorModal(
        error as ErrorType,
        `Class: ${className} (${classId})\nAssistant: ${selectedAssistant?.name || "Unknown"} (${selectedAssistantId || "Unknown"})`
      )
    } finally {
      setIsPending(false)
    }
  }

  if (!open) return null

  return (
    <motion.div
      initial={{ width: 600 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={{ overflow: "hidden" }}
    >
      <Modal
        title={
          isEdit
            ? `Edit assistant in ${className}`
            : `Add assistant to ${className}`
        }
        onClose={onClose}
        open={open}
        width={600}
        onOk={handleSubmit}
        loading={isPending}
      >
        {/* Header */}
        <Row style={{ margin: "20px 0" }}>
          <Col span={12}>
            {isEdit ? (
              <Flex>
                <ClickableStudentInfoRow student={selectedAssistant} border />
              </Flex>
            ) : (
              <Select
                showSearch
                filterOption={() => true} // Need this for our fuzzy search to work
                onSearch={onSearch}
                style={{ width: 300, marginTop: 10 }}
                value={selectedAssistantId}
                onChange={setSelectedAssistantId}
                placeholder="Select assistant"
                options={
                  filteredAssistantOptions as {
                    key: string
                    label: string
                    value: string
                  }[]
                }
                allowClear
              />
            )}
          </Col>
        </Row>

        <Row
          gutter={40}
          style={{ transition: "all 0.3s ease-out", flexWrap: "nowrap" }}
        >
          {/* Sessions */}
          <Col
            span={24}
            style={{ transition: "all 0.3s ease-out", maxWidth: 580 }}
          >
            <Row>
              <Col span={24}>
                {selectedAssistant ? (
                  <ClassSessionSelectionGrid
                    attendanceForClass={attendanceForClass}
                    classSessions={sortedClassSessions}
                    selectedSessions={selectedSessions}
                    setSelectedSessions={setSelectedSessions}
                    disabledMessage="Sessions that the assistant attended can not be changed."
                    sessionMetadata={sessionMetadata}
                    newlySelectedSessions={newlySelectedSessions}
                    droppedSessions={droppedSessions}
                  />
                ) : (
                  <Flex col gap={10}>
                    <h2>Sessions:</h2>
                    <Flex>
                      <h2>No assistant selected</h2>
                    </Flex>
                  </Flex>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </Modal>
    </motion.div>
  )
}

export default AddAssistantToClassForm
